import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../client';
import { connectDraftStepsApi, disconnectDraftStepsApi } from '../../persist/draft';
import { useDraftMutation } from '../helpers/draftMutation';
import { appendMissingPositions } from '../helpers/placeholderStep';
import {
  addSourceToNodeInputs,
  applyEdgeChangeToTarget,
  removeSourceFromNodeInputs
} from '../helpers/draftGraphOperations';
import {
  applyDraftCache,
  optimisticDraftUpdate,
  restoreDraftCache
} from '../helpers/draftCacheHelpers';
import { reconcileEnvelope } from '../../draftGraph';
import { processLayoutKeys } from '../../query-keys';
import {
  type AddSuccessorStepsRequest,
  type ConnectDraftStepsRequest,
  type DraftNodeEnvelope,
  type GetProcessLayoutResponse
} from '@bliks-io/contracts';
import { expandRuleIdToOutputGroup } from '../../../pages/processes/editor/draft/graph/expandRuleIdToOutputGroup';
import { basePath } from './shared';

export function useConnectDraftSteps(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: ({
      stepId,
      body
    }: {
      stepId: string;
      body: ConnectDraftStepsRequest;
    }): Promise<DraftNodeEnvelope> => connectDraftStepsApi(processId, stepId, body),
    onMutate: async ({ stepId: sourceId, body: { targetStepId, ruleId } }) => {
      return optimisticDraftUpdate(queryClient, processId, (old) => {
        const sourceRules = old.steps.find((s) => s._id === sourceId)?.rules;
        const expanded = ruleId ? expandRuleIdToOutputGroup(sourceRules, ruleId) : [];
        let ruleIdsForInput: string[] | undefined;
        if (expanded.length > 0) ruleIdsForInput = expanded;
        else if (ruleId) ruleIdsForInput = [ruleId];
        return applyEdgeChangeToTarget(old, sourceId, targetStepId, (target, sourceStable) =>
          addSourceToNodeInputs(target, sourceStable, ruleIdsForInput)
        );
      });
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

export function useDisconnectDraftSteps(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: ({
      stepId,
      targetStepId,
      ruleId
    }: {
      stepId: string;
      targetStepId: string;
      ruleId?: string;
    }): Promise<DraftNodeEnvelope> =>
      disconnectDraftStepsApi(processId, stepId, targetStepId, ruleId),
    onMutate: async ({ stepId: sourceId, targetStepId, ruleId }) => {
      return optimisticDraftUpdate(queryClient, processId, (old) => {
        // Output-scoped removal: expand the clicked rule to its output group so
        // the optimistic trim matches the server (other outputs of the same
        // decision survive). No ruleId → whole-connection removal.
        const sourceRules = old.steps.find((s) => s._id === sourceId)?.rules;
        const expanded = ruleId ? expandRuleIdToOutputGroup(sourceRules, ruleId) : [];
        let outputRuleIds: string[] | undefined;
        if (expanded.length > 0) outputRuleIds = expanded;
        else if (ruleId) outputRuleIds = [ruleId];
        return applyEdgeChangeToTarget(old, sourceId, targetStepId, (target, sourceStable) =>
          removeSourceFromNodeInputs(target, sourceStable, outputRuleIds)
        );
      });
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Materialize picked successors of a manual task step as downstream draft
 * assignments. The backend creates one `ProcessDraftAssignment` per newly-added
 * entity and recomputes the parent's `task.successors`. Response is
 * authoritative — write directly into the cache so the UI converges before
 * the settle backstop's refetch lands.
 */
export function useAddDraftSuccessorSteps(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      stepId,
      body
    }: {
      stepId: string;
      body: AddSuccessorStepsRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/steps/${stepId}/successor-steps`,
        body
      );
      return data;
    },
    onSuccess: (envelope, { body }) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));

      // Without the optimistic seed, each new node flashes through the
      // canvas-origin sentinel at `{0,0}` before the layout refetch completes.
      const position = body.position;
      const layoutKey = processLayoutKeys.detail(processId, 'draft');
      const createdIds = envelope.createdNodeIds.map(String);
      if (position && createdIds.length > 0) {
        const created = createdIds.map((stableStepId) => ({
          stableStepId,
          x: position.x,
          y: position.y
        }));
        queryClient.setQueryData<GetProcessLayoutResponse>(layoutKey, (prev) =>
          prev ? appendMissingPositions(prev, created) : prev
        );
      }
    }
  });
}
